import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import * as authSlice from '../auth/authSlice';
import * as api from './signupAPI';

const initialState = {
  step: 1,
  status: 'idle',
  createAccountStatus: 'idle',
  createAccountErrorReason: '',
  loginStatus: 'idle',
  resetStatus: 'idle',
  emailVerificationSendStatus: 'idle',
  emailVerificationSendError: '',
  email: '',
  templates: [],
  selectedTemplate: null,
  accessToken: null,
  idToken: null,
  tokenProvider: null,
  userBaseInfoModalOpenCount: 0,
  continueByPhoneClosed: false,
  continueByPhoneStatus: 'idle',
  loginErrorMessage: ''
};

export const fetchSignupTemplates = createAsyncThunk(
  'signup/fetchSignupTemplates',
  async (locale) => {
    const response = await api.fetchSignupTemplates(locale);
    return response.body;
  }
);

export const createAccount = createAsyncThunk(
  'signup/createAccount',
  async (params, { getState }) => {
    const response = await api.createAccount({
      email: getState().signup.email,
      selectedTemplate: getState().signup.selectedTemplate.label,
      locale: params,
      idToken: getState().signup.idToken,
      tokenProvider: getState().signup.tokenProvider,
      accessToken: getState().signup.accessToken,
    });

    return response;
  }
);

export const login = createAsyncThunk(
  'signup/login',
  async (password, { getState }) => {
    const response = await api.login({
      email: getState().signup.email,
      password,
      idToken: getState().signup.idToken,
      tokenProvider: getState().signup.tokenProvider,
      accessToken: getState().signup.accessToken,
    });

    return response;
  }
);

export const sendPasswordReset = createAsyncThunk(
  'signup/sendPasswordReset',
  async (_params, { getState }) => {
    const response = await api.sendPasswordReset({
      email: getState().signup.email,
    });

    return response;
  }
);

export const sendEmailVerificationEmail = createAsyncThunk(
  'signup/sendEmailVerificationEmail',
  async (_params, { getState, dispatch }) => {
    const response = await api.sendEmailVerificationEmail(getState().auth.data.email);

    dispatch(authSlice.getAuth());
    return response;
  }
);

export const sendLoginEmail = createAsyncThunk(
  'signup/sendLoginEmail',
  async (_params, { getState }) => {
    const response = await api.sendLoginEmail(getState().auth.data.email);

    return response;
  }
);

export const signupSlice = createSlice({
  name: 'signup',
  initialState,
  reducers: {
    setStep: (state, action) => {
      state.step = action.payload;
    },
    setEmail: (state, action) => {
      state.email = action.payload
    },
    setSelectedTemplate: (state, action) => {
      state.selectedTemplate = action.payload
    },
    setIdToken: (state, action) => {
      state.idToken = action.payload;
    },
    setAccessToken: (state, action) => {
      state.accessToken = action.payload;
    },
    setTokenProvider: (state, action) => {
      state.tokenProvider = action.payload;
    },
    resetAll: (state) => {
      state.tokenProvider = null;
      state.accessToken = null;
      state.idToken = null;
      state.selectedTemplate = null;
      state.email = '';
    },
    addUserBaseInfoModalOpenCount: (state) => {
      state.userBaseInfoModalOpenCount++;
    },
    setContinueByPhoneClosed: (state, action) => {
      state.continueByPhoneClosed = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSignupTemplates.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchSignupTemplates.fulfilled, (state, action) => {
        state.templates = action.payload.results;
        state.status = 'idle';
      })
      .addCase(fetchSignupTemplates.rejected, (state) => {
        state.status = 'error';
      })
      .addCase(createAccount.pending, (state) => {
        state.createAccountStatus = 'loading';
        state.step = 4;
      })
      .addCase(createAccount.fulfilled, (state, action) => {
        state.createAccountStatus = 'idle';

        if (action.payload.body.loggedIn) {
          const pathName = window.location;
          window.location.replace(pathName.origin + '/newui/folders?fromSignup=1');
        } else if (action.payload.body.redirectToLogin) {
          const pathName = window.location;
          window.location.replace(pathName.origin + '/login');
        } else {
          state.createAccountStatus = 'ready';
        }
      })
      .addCase(createAccount.rejected, (state, action) => {
        state.createAccountStatus = 'error';
        state.createAccountErrorReason = action?.error?.message;
      })
      .addCase(login.pending, (state) => {
        state.loginStatus = 'loading';
        state.loginErrorMessage = '';
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loginStatus = 'idle';

        const params = new URLSearchParams(window.location.search);
        let path = params?.get('targetUrl');
        if (!path) {
          if (action?.payload?.body?.targetUrl) {
            path = action.payload.body.targetUrl;
          } else {
            path = action?.payload?.body?.signupRoute === 'quick-trial' ? '/newui' : '';
          }
        }
        const pathName = window.location;
        window.location.replace(pathName.origin + path);
      })
      .addCase(login.rejected, (state, action) => {
        state.loginErrorMessage = action.error.message;
        state.loginStatus = 'error';
        state.idToken = null;
        state.tokenProvider = null;
        state.accessToken = null;
      })
      .addCase(sendPasswordReset.pending, (state) => {
        state.resetStatus = 'loading';
      })
      .addCase(sendPasswordReset.fulfilled, (state) => {
        state.resetStatus = 'success';
      })
      .addCase(sendPasswordReset.rejected, (state) => {
        state.resetStatus = 'error';
      })
      .addCase(sendEmailVerificationEmail.pending, (state) => {
        state.emailVerificationSendStatus = 'sending';
        state.emailVerificationSendError = '';
      })
      .addCase(sendEmailVerificationEmail.fulfilled, (state) => {
        state.emailVerificationSendStatus = 'idle';
      })
      .addCase(sendEmailVerificationEmail.rejected, (state, action) => {
        state.emailVerificationSendStatus = 'error';
        state.emailVerificationSendError = action.error?.message;
      })
      .addCase(sendLoginEmail.pending, (state) => {
        state.continueByPhoneStatus = 'sending';
      })
      .addCase(sendLoginEmail.fulfilled, (state) => {
        state.continueByPhoneStatus = 'ready';
      })
      .addCase(sendLoginEmail.rejected, (state) => {
        state.continueByPhoneStatus = 'error';
      });
  }
});

export const {
  setStep,
  setEmail,
  setSelectedTemplate,
  setIdToken,
  setAccessToken,
  setTokenProvider,
  resetAll,
  addUserBaseInfoModalOpenCount,
  setContinueByPhoneClosed
} = signupSlice.actions;

export const selectTemplates = (state) => state.signup.templates;
export const selectStatus = (state) => state.signup.status;
export const selectCreateAccountStatus = (state) => state.signup.createAccountStatus;
export const selectCreateAccountErrorReason = (state) => state.signup.createAccountErrorReason;
export const selectLoginStatus = (state) => state.signup.loginStatus;
export const selectResetStatus = (state) => state.signup.resetStatus;
export const selectStep = (state) => state.signup.step;
export const selectEmail = (state) => state.signup.email;
export const selectSelectedTemplate = (state) => state.signup.selectedTemplate;
export const selectEmailVerificationSendStatus = (state) => state.signup.emailVerificationSendStatus;
export const selectEmailVerificationSendError = (state) => state.signup.emailVerificationSendError;
export const selectTokenProvider = (state) => state.signup.tokenProvider;
export const selectUserBaseInfoModalOpenCount = (state) => state.signup.userBaseInfoModalOpenCount;
export const selectContinueByPhoneClosed = (state) => state.signup.continueByPhoneClosed;
export const selectContinueByPhoneStatus = (state) => state.signup.continueByPhoneStatus;
export const selectLoginErrorMessage = (state) => state.signup.loginErrorMessage;

export default signupSlice.reducer;
